import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip, LabelList,
} from 'recharts';
import type { BreakdownResult } from '@/hooks/useCallOutcomesBreakdown';
import { getOutcomeColor } from '@/lib/analytics/outcomeColors';
import { useMemo } from 'react';

interface Props {
  data: BreakdownResult;
  canonicalOrder: string[];
  onClickSegment?: (outcome: string) => void;
}

export function CallOutcomesChart({ data, canonicalOrder, onClickSegment }: Props) {
  const rows = useMemo(() => {
    const byOutcome = new Map(data.rows.map((r) => [r.segment, r]));
    const inOrder = canonicalOrder.map((name) => {
      const r = byOutcome.get(name);
      return {
        name,
        value: r?.current_count ?? 0,
        percent: r?.current_percent ?? 0,
        outcomeKey: name,
      };
    });
    const unrecorded = byOutcome.get('no_outcome');
    if (unrecorded) {
      inOrder.push({
        name: 'Unrecorded',
        value: unrecorded.current_count,
        percent: unrecorded.current_percent,
        outcomeKey: 'no_outcome',
      });
    }
    return inOrder;
  }, [data.rows, canonicalOrder]);

  return (
    <ResponsiveContainer width="100%" height={Math.max(256, rows.length * 28)}>
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 48 }}>
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="name"
          width={160}
          fontSize={12}
          stroke="hsl(var(--muted-foreground))"
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--popover))',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--popover-foreground))',
          }}
          formatter={(v, _name, item) => {
            const percent = (item?.payload as { percent?: number } | undefined)?.percent ?? 0;
            return [`${v} (${percent}%)`, 'Count'];
          }}
        />
        <Bar
          dataKey="value"
          minPointSize={2}
          onClick={(d: { outcomeKey?: string }) => d.outcomeKey && onClickSegment?.(d.outcomeKey)}
          cursor={onClickSegment ? 'pointer' : 'default'}
        >
          {rows.map((r, i) => (
            <Cell key={i} fill={getOutcomeColor(r.name, canonicalOrder)} />
          ))}
          <LabelList dataKey="value" position="right" fontSize={11} fill="hsl(var(--foreground))" />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
